import { useEffect, useRef, type ReactNode } from "react";

type Props = {
  label?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
};

const HTML_RE = /<[a-z][\s\S]*>/i;

function escapeAndBreak(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
}

/**
 * Éditeur de texte enrichi minimaliste (sans dépendance) : gras, italique,
 * souligné, barré, listes, alignement / justifié. Produit du HTML simple qui est
 * ré-assaini côté serveur avant stockage (services/sanitize.ts).
 */
export function RichTextField({ label, value, onChange, placeholder }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  // Entrée = nouveau <p> (et non <div>) → balisage homogène et propre.
  useEffect(() => {
    try {
      document.execCommand("defaultParagraphSeparator", false, "p");
    } catch {
      /* API dépréciée mais tolérée ; sans effet si indisponible */
    }
  }, []);

  // Ne réécrit le DOM que lorsque la valeur change « de l'extérieur » (ouverture
  // d'une autre fiche). Pendant la frappe on laisse le navigateur maître du DOM,
  // sinon le curseur saute à chaque caractère.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = HTML_RE.test(value) ? value : escapeAndBreak(value ?? "");
    if (next !== el.innerHTML) el.innerHTML = next;
  }, [value]);

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const exec = (cmd: string, arg?: string) => {
    document.execCommand(cmd, false, arg);
    emit();
  };

  const Btn = ({
    cmd,
    arg,
    title,
    children,
  }: {
    cmd: string;
    arg?: string;
    title: string;
    children: ReactNode;
  }) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      // onMouseDown + preventDefault : conserve la sélection dans l'éditeur.
      onMouseDown={(e) => {
        e.preventDefault();
        exec(cmd, arg);
      }}
      className="rte-btn"
    >
      {children}
    </button>
  );

  return (
    <div>
      {label && (
        <span className="mb-1 block text-sm font-medium text-ink-700">{label}</span>
      )}
      <div className="rounded-md border border-ink-300 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/30">
        <div className="flex flex-wrap items-center gap-0.5 border-b border-ink-200 p-1">
          <Btn cmd="bold" title="Gras">
            <b>G</b>
          </Btn>
          <Btn cmd="italic" title="Italique">
            <i>I</i>
          </Btn>
          <Btn cmd="underline" title="Souligné">
            <u>U</u>
          </Btn>
          <Btn cmd="strikeThrough" title="Barré">
            <s>B</s>
          </Btn>
          <span className="mx-1 h-5 w-px bg-ink-200" />
          <Btn cmd="insertUnorderedList" title="Liste à puces">
            •
          </Btn>
          <Btn cmd="insertOrderedList" title="Liste numérotée">
            1.
          </Btn>
          <span className="mx-1 h-5 w-px bg-ink-200" />
          <Btn cmd="justifyLeft" title="Aligner à gauche">
            ⇤
          </Btn>
          <Btn cmd="justifyCenter" title="Centrer">
            ≡
          </Btn>
          <Btn cmd="justifyRight" title="Aligner à droite">
            ⇥
          </Btn>
          <Btn cmd="justifyFull" title="Justifier">
            ☰
          </Btn>
          <span className="mx-1 h-5 w-px bg-ink-200" />
          <Btn cmd="removeFormat" title="Effacer la mise en forme">
            ✕
          </Btn>
        </div>
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          data-placeholder={placeholder}
          onInput={emit}
          onBlur={emit}
          className="rte-content"
        />
      </div>
    </div>
  );
}
